import { PAGE_SIZE_OPTIONS } from '@noor/config/constants';
import { cn } from '@noor/lib/cn';
import type { Table } from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

export type PaginationItem = number | 'ellipsis';

export interface PaginationLabels {
  /** Leading "Show" caption of the page-size cluster. */
  show?: string;
  /**
   * Trailing "records" caption of the page-size cluster.
   * @deprecated Redundant next to the centered "Всего X из Y" caption;
   * kept for compatibility and ignored by the component.
   */
  records?: string;
  /** Pre-formatted "Page X of Y" caption (legacy fallback when `totalItems` is omitted). */
  pageOf?: string;
  /** Pre-formatted "Всего X из Y" caption (overrides the translated template). */
  totalOf?: string;
  /** Previous-button label (used as its aria-label; icon-only per Figma). */
  prev?: string;
  /** Next-button label (used as its aria-label; icon-only per Figma). */
  next?: string;
}

/**
 * Items visible on the given 1-based page: a full `pageSize`, except the last
 * page which may be partial (never negative).
 */
export function visibleItemCount(
  page: number,
  pageSize: number,
  totalItems: number,
): number {
  return Math.min(pageSize, Math.max(totalItems - (page - 1) * pageSize, 0));
}

export interface PaginationProps {
  /** Current page, 1-based. */
  page: number;
  /** Current page size. */
  pageSize: number;
  /** Total page count (>= 1). */
  pageCount: number;
  /**
   * Total item count. Renders the Figma "Всего X из Y" caption, where X is the
   * number of items on the current page. When omitted the caption falls back
   * to the legacy "Page X of Y" template.
   */
  totalItems?: number | undefined;
  onPageChange: (page: number) => void;
  /** When omitted (or `showPageSize` is false) the page-size cluster hides. */
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  showPageSize?: boolean;
  /** Overrides for the translated captions (tests, stories, custom copy). */
  labels?: PaginationLabels;
  className?: string;
  /**
   * Footer geometry. Defaults to `pill`; `rounded` renders the Noor-CPanel
   * merchant dialog footer: a gray-100 12px-radius page-size trigger with no
   * "Show" caption, and 12px-radius white steppers.
   */
  shape?: 'pill' | 'rounded';
}

/**
 * Compute the windowed page list (Figma 843:23475 — active=2 of 23 reads
 * `[1, 2, 3, 4, 5, 6, 'ellipsis', 21, 22, 23]`): a six-page head block near
 * the start, a mirrored three-page head + six-page tail near the end, and
 * the current±2 window with two ellipses in the middle, so the active page
 * always carries two neighbours on each side (page 4 reads 2, 3, 4, 5, 6).
 * A one-page gap reads better as the page itself than as an ellipsis.
 */
export function paginationItems(page: number, pageCount: number): PaginationItem[] {
  const total = Math.max(pageCount, 1);
  const current = Math.min(Math.max(page, 1), total);
  if (total <= 8) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let windowed: number[];
  if (current <= 4) {
    windowed = [1, 2, 3, 4, 5, 6, total - 2, total - 1, total];
  } else if (current >= total - 3) {
    windowed = [1, 2, 3, total - 5, total - 4, total - 3, total - 2, total - 1, total];
  } else {
    windowed = [1, current - 2, current - 1, current, current + 1, current + 2, total];
  }

  const sorted = [...new Set(windowed)]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);

  const result: PaginationItem[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev === 2) result.push(prev + 1);
    else if (p - prev > 2) result.push('ellipsis');
    result.push(p);
    prev = p;
  }
  return result;
}

/**
 * Figma-exact table pagination (node 843:23475, moderation node 982:8869).
 * Left: the "Показать [10]" cluster — 14px Medium label around a white
 * borderless pill (pl-20px/pr-10px/py-10px, 16px chevron); the trailing
 * "записи" word is intentionally omitted as redundant next to the totals
 * caption. Center: the "Всего X из Y" caption (`flex-1 text-center`).
 * Right: the windowed page numbers (idle #9ca3af, active black on the
 * #f5f5f5 wash, 4px radius, 8px side padding) plus two 40px white circular
 * icon-only steppers (1px #e5e7eb border, 16px chevrons, 7px gap). Numbers
 * and steppers stay one group so a wide footer can't balloon the gap
 * between them.
 *
 * Fully controlled — the parent owns page/pageSize (URL params for
 * server-driven tables). `DataTablePagination` below binds it to a TanStack
 * table instance for the client-paginated case.
 */
export function Pagination({
  page,
  pageSize,
  pageCount,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  showPageSize = true,
  labels,
  className,
  shape = 'pill',
}: PaginationProps) {
  const { t } = useTranslation('common');
  const total = Math.max(pageCount, 1);
  const current = Math.min(Math.max(page, 1), total);
  const items = paginationItems(current, total);

  const showLabel = labels?.show ?? t('pagination.show');
  const pageOfLabel =
    labels?.pageOf ?? t('pagination.pageOf', { page: current, count: total });
  const prevLabel = labels?.prev ?? t('pagination.prev');
  const nextLabel = labels?.next ?? t('pagination.next');

  // Items visible on the current page (last page may be partial).
  const shown =
    totalItems === undefined
      ? undefined
      : visibleItemCount(current, pageSize, totalItems);
  const caption =
    labels?.totalOf ??
    (shown === undefined
      ? pageOfLabel
      : t('pagination.totalOf', { shown, total: totalItems }));

  const showSizeCluster = showPageSize && onPageSizeChange !== undefined;

  const sizeCluster = showSizeCluster ? (
    <div className="flex items-center gap-2">
      {shape === 'pill' ? (
        <span className="text-[length:var(--pagination-font-size)] font-[var(--pagination-font-weight)] tracking-[var(--pagination-tracking)] text-pagination-label-fg">
          {showLabel}
        </span>
      ) : null}
      <Select
        value={String(pageSize)}
        onValueChange={(value) => onPageSizeChange(Number(value))}
      >
        <SelectTrigger
          size="sm"
          aria-label={showLabel}
          className={cn(
            'h-auto! min-w-20 justify-between gap-2.5 border-0 bg-filter-bg py-2.5 pr-2.5 pl-5 text-[length:var(--pagination-font-size)] leading-none font-[var(--pagination-font-weight)] tracking-[var(--pagination-tracking)] text-pagination-label-fg shadow-none [&_svg]:size-4 [&_svg]:text-pagination-label-fg',
            shape === 'pill'
              ? 'rounded-[var(--pagination-stepper-radius-pill)]'
              : 'rounded-[var(--pagination-stepper-radius)] bg-gray-100',
          )}
        >
          <SelectValue placeholder={String(pageSize)} />
        </SelectTrigger>
        <SelectContent>
          {pageSizeOptions.map((size) => (
            <SelectItem key={size} value={String(size)}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : (
    <span />
  );

  // Centered totals caption per Figma 982:8869 — the `rounded` shape docks it
  // left, right after the size cluster (merchant dialog footer).
  const totalsCaption = (
    <span
      data-testid="pagination-total"
      className={cn(
        'text-[length:var(--pagination-font-size)] font-[var(--pagination-font-weight)] tracking-[var(--pagination-tracking)] whitespace-nowrap text-pagination-label-fg',
        shape === 'pill' ? 'flex-1 text-center' : 'flex-none text-left',
      )}
    >
      {caption}
    </span>
  );

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex flex-wrap items-center justify-between gap-4', className)}
    >
      {shape === 'pill' ? (
        <>
          {sizeCluster}
          {totalsCaption}
        </>
      ) : (
        <div className="flex items-center gap-5">
          {sizeCluster}
          {totalsCaption}
        </div>
      )}

      {/* Numbers and steppers docked as one end group, so a wide footer
          can't balloon the gap between the numbers and the arrows. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-4">
        <div className="flex items-center gap-1">
          {items.map((item, i) =>
            item === 'ellipsis' ? (
              <span
                key={`ellipsis-${i}`}
                aria-hidden="true"
                className="px-[var(--pagination-item-padding-x)] text-[length:var(--pagination-font-size)] font-[var(--pagination-font-weight)] tracking-[var(--pagination-tracking)] text-[#767676] select-none"
              >
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                aria-label={`Page ${item}`}
                aria-current={item === current ? 'page' : undefined}
                onClick={() => onPageChange(item)}
                className={cn(
                  'cursor-pointer rounded-[var(--pagination-item-radius)] px-[var(--pagination-item-padding-x)] text-[length:var(--pagination-font-size)] font-[var(--pagination-font-weight)] tracking-[var(--pagination-tracking)] tabular-nums transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40',
                  item === current
                    ? 'bg-pagination-item-bg-active text-pagination-item-fg-active'
                    : 'text-pagination-item-fg hover:bg-pagination-item-bg-active hover:text-pagination-item-fg-active',
                )}
              >
                {item}
              </button>
            ),
          )}
        </div>

        <div className="flex items-center gap-[7px]">
          <button
            type="button"
            aria-label={prevLabel}
            onClick={() => onPageChange(current - 1)}
            disabled={current <= 1}
            className={cn(
              'flex h-[var(--pagination-stepper-size)] w-[var(--pagination-stepper-size)] cursor-pointer items-center justify-center border border-filter-border bg-filter-bg text-pagination-label-fg transition-colors outline-none hover:bg-pagination-item-bg-active focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-40',
              shape === 'pill'
                ? 'rounded-[var(--pagination-stepper-radius-pill)]'
                : 'rounded-[var(--pagination-stepper-radius)]',
            )}
          >
            <ChevronLeftIcon className="size-4" />
          </button>
          <button
            type="button"
            aria-label={nextLabel}
            onClick={() => onPageChange(current + 1)}
            disabled={current >= total}
            className={cn(
              'flex h-[var(--pagination-stepper-size)] w-[var(--pagination-stepper-size)] cursor-pointer items-center justify-center border border-filter-border bg-filter-bg text-pagination-label-fg transition-colors outline-none hover:bg-pagination-item-bg-active focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-40',
              shape === 'pill'
                ? 'rounded-[var(--pagination-stepper-radius-pill)]'
                : 'rounded-[var(--pagination-stepper-radius)]',
            )}
          >
            <ChevronRightIcon className="size-4" />
          </button>
        </div>
      </div>
    </nav>
  );
}

export interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  pageSizeOptions?: readonly number[];
  showPageSize?: boolean;
  labels?: PaginationLabels;
  className?: string;
  shape?: 'pill' | 'rounded';
}

/** `Pagination` bound to a TanStack table's manual-pagination state. */
export function DataTablePagination<TData>({
  table,
  pageSizeOptions,
  showPageSize,
  labels,
  className,
  shape,
}: DataTablePaginationProps<TData>) {
  const { pageIndex, pageSize } = table.getState().pagination;
  return (
    <Pagination
      page={pageIndex + 1}
      pageSize={pageSize}
      pageCount={table.getPageCount()}
      totalItems={
        table.options.manualPagination
          ? table.options.rowCount
          : table.getPrePaginationRowModel().rows.length
      }
      onPageChange={(next) => table.setPageIndex(next - 1)}
      onPageSizeChange={(next) => table.setPagination({ pageIndex: 0, pageSize: next })}
      {...(pageSizeOptions !== undefined ? { pageSizeOptions } : {})}
      {...(showPageSize !== undefined ? { showPageSize } : {})}
      {...(labels !== undefined ? { labels } : {})}
      {...(className !== undefined ? { className } : {})}
      {...(shape !== undefined ? { shape } : {})}
    />
  );
}
